import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '@/lib/supabase';

interface AISuggestionButtonProps {
  field: 'yesterday' | 'today' | 'blockers';
  content: string;
  onSuggestion: (suggestion: string) => void;
}

export function AISuggestionButton({ field, content, onSuggestion }: AISuggestionButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (!content.trim()) {
      setError('Please enter some content first');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      console.log('Calling improve-scrum with:', { content, field });
      
      const { data, error } = await supabase.functions.invoke('improve-scrum', {
        body: { 
          content: content,  
          field: field      
        }
      });

      console.log('Response:', { data, error });

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      if (!data?.suggestion) {
        console.error('No suggestion in response:', data);
        throw new Error('No suggestion received');
      }

      onSuggestion(data.suggestion);
    } catch (err) {
      console.error('Error getting suggestion:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to get suggestion';
      console.error('Error details:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative inline-block">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleClick}
        disabled={loading || !content.trim()}
        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
      >
        <Sparkles className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
        <span>{loading ? 'Improving...' : 'Enhance with AI'}</span>
      </Button>
      
      {error && (
        <div className="absolute top-full right-0 mt-1 z-10 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs px-2 py-1 rounded-md whitespace-nowrap shadow-sm border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}
    </div>
  );
}
